import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface UseAdminNavScrollRetentionOptions {
  scrollContainerSelector?: string;
  navItemSelector?: string;
  enableDebug?: boolean;
}

export const useAdminNavScrollRetention = (options: UseAdminNavScrollRetentionOptions = {}) => {
  const {
    scrollContainerSelector = '[data-sidebar="true"]',
    navItemSelector = 'a[href*="/admin/"]',
    enableDebug = false
  } = options;
  
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const [isReady, setIsReady] = useState(false);
  
  const debug = useCallback((message: string, data?: any) => {
    if (enableDebug) {
      console.log(`[AdminNavScroll] ${message}`, data || '');
    }
  }, [enableDebug]);

  // Always log critical events for debugging
  const log = useCallback((message: string, data?: any) => {
    console.log(`[AdminNavScroll] ${message}`, data || '');
  }, []);

  // Get scroll container element
  const getScrollContainer = useCallback((): HTMLElement | null => {
    if (scrollContainerRef.current) {
      return scrollContainerRef.current;
    }
    
    const container = document.querySelector(scrollContainerSelector) as HTMLElement;
    if (container) {
      scrollContainerRef.current = container;
      debug('Found scroll container:', container);
    }
    return container;
  }, [scrollContainerSelector, debug]);

  // Find the nav item for the current path
  const findCurrentNavItem = useCallback((): HTMLElement | null => {
    const container = getScrollContainer();
    if (!container) return null;

    // Find nav item that matches current pathname
    const navItems = container.querySelectorAll(navItemSelector);
    for (let i = 0; i < navItems.length; i++) {
      const item = navItems[i] as HTMLElement;
      const href = item.getAttribute('href');
      if (href === location.pathname) {
        log('Found current nav item:', { href, element: item });
        return item;
      }
    }

    // Try partial match if exact match not found
    for (let i = 0; i < navItems.length; i++) {
      const item = navItems[i] as HTMLElement;
      const href = item.getAttribute('href');
      if (href && location.pathname.startsWith(href) && href !== '/admin') {
        log('Found partial match nav item:', { href, pathname: location.pathname, element: item });
        return item;
      }
    }

    debug('No matching nav item found for:', location.pathname);
    return null;
  }, [getScrollContainer, navItemSelector, location.pathname, log, debug]);

  // Scroll to center the current nav item
  const scrollToCurrentPage = useCallback(() => {
    const container = getScrollContainer();
    if (!container || isScrollingRef.current) {
      return;
    }

    const navItem = findCurrentNavItem();
    if (!navItem) {
      debug('No nav item to scroll to');
      return;
    }

    isScrollingRef.current = true;

    // Calculate scroll position to center the item
    const containerRect = container.getBoundingClientRect();
    const itemRect = navItem.getBoundingClientRect();
    
    // Calculate item position relative to container
    const itemTopRelativeToContainer = itemRect.top - containerRect.top + container.scrollTop;
    const itemHeight = itemRect.height;
    const containerHeight = containerRect.height;
    
    // Center the item in the viewport
    const targetScrollTop = itemTopRelativeToContainer - (containerHeight / 2) + (itemHeight / 2);
    
    // Ensure we don't scroll beyond bounds
    const maxScrollTop = container.scrollHeight - containerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

    log('🎯 Centering nav item:', { 
      pathname: location.pathname,
      itemTopRelativeToContainer,
      targetScrollTop,
      finalScrollTop,
      containerHeight,
      itemHeight
    });

    // Smooth scroll to position
    container.scrollTo({
      top: finalScrollTop,
      behavior: 'smooth'
    });

    // Mark scrolling as complete after animation
    setTimeout(() => {
      isScrollingRef.current = false;
      log('✅ Scroll to current page completed');
    }, 300);
    
  }, [getScrollContainer, findCurrentNavItem, location.pathname, log, debug]);

  // Handle navigation - scroll to current page
  useEffect(() => {
    log('🧭 Navigation detected:', location.pathname);
    
    // Scroll to current page after a short delay to allow DOM to update
    const scrollTimer = setTimeout(() => {
      scrollToCurrentPage();
    }, 100);
    
    return () => clearTimeout(scrollTimer);
  }, [location.pathname, scrollToCurrentPage, log]);

  // Initialize and scroll to current page when component mounts
  useEffect(() => {
    const initTimer = setTimeout(() => {
      const container = getScrollContainer();
      if (container) {
        setIsReady(true);
        
        // Scroll to current page
        scrollToCurrentPage();
        
        log('✅ Initialization completed');
      } else {
        debug('Scroll container not found after delay');
      }
    }, 150); // Slightly longer delay to ensure nav items are rendered

    return () => {
      clearTimeout(initTimer);
    };
  }, [getScrollContainer, scrollToCurrentPage, log, debug]);

  return {
    scrollToCurrentPage,
    scrollPosition: scrollContainerRef.current?.scrollTop || 0,
    isReady,
    debug
  };
};
